import { Injectable, NgZone, signal, WritableSignal, inject } from "@angular/core";
import { Router } from "@angular/router";
import { UntypedFormGroup } from "@angular/forms";
import { Tab } from "@app/shared/models/tab.model";
import { Subject } from "rxjs";
import { ROUTES } from "@app/shared/constants/routes.constants";
import { NotifyService } from "@app/core/services/notify.service";
import { MenuOption } from "@app/shared/models/menu.model";
import { ServiceWorkerService } from "@app/core/services/service-worker.service";
import { MENU_OPTIONS } from "@app/shared/constants/menu-options.constants";
import { ExternalWindowHandlerService } from "@app/core/services/external-window-handler.service";
import { ExternalWindowEventTypes } from "@app/shared/models/external-window-handler.model";
import { AppInjector } from "@app/core/functions/app-injector";
import { AuthenticationService } from "./authentication.service";
import { MessageService } from "@app/core/services/message.service";

@Injectable({
  providedIn: "root"
})
export class TabService {
  private router = inject(Router);
  private ng = inject(NgZone);
  private notifyService = inject(NotifyService);
  private swService = inject(ServiceWorkerService);
  private authService = inject(AuthenticationService);
  private messageService = inject(MessageService);

  tabs: WritableSignal<Tab[]> = signal([]);
  selectedIndex: number = 0;
  refreshActiveTab = new Subject<void>();

  /**
   * Sets the form for the current tabs index to check before closing
   */
  set tabForm(form: UntypedFormGroup) {
    this.activeTab.form = form;
  }

  /**
   * Sets the tabs form to undefined.
   * Used in special cases where we want read only processing
   * @param tab
   */
  removeTabForm(tab: Tab): void {
    tab.form = undefined;
  }

  /**
   * Remove requested tab from array
   * Also handles special cases for routing the user to the correct tab
   * @param tab
   */
  removeTab(tab: Tab): void {
    this._destroyTab(tab);
  }

  /**
   * Remove the currently opened webface tab
   * NOTE: There will only ever be one tab opened for a webfacing session
   */
  removeWebFaceTab(): void {
    const tab = this.tabs().find((x) => x.componentType === MENU_OPTIONS.IFRAME.id);
    if (tab) {
      this._destroyTab(tab, true);
    }
  }

  /**
   * Remove the currently opened PDF Viewer
   * NOTE: There will only ever be one tab opened for a pdf viewer
   */
  removePdfViewerTab(): void {
    const tab = this.pdfTab;
    if (tab) {
      this.externalWindowService.postMessage(this.pdfTab.externalWindowRef, true, ExternalWindowEventTypes.Hidden);
      tab?.form?.markAsPristine();
      this._destroyTab(tab);
    }
  }

  /**
   * Closes currently selected tab by index
   * Typically used for Iframes or the Close button in the component
   *
   * NOTE: This is used for the webfacing screens when they return
   *       and need to close the current tab.
   *
   *       Do NOT use this method. Use removeTab(tab) for all other components.
   *
   */
  removeCurrentTab(): void {
    try {
      this._destroyTab(this.activeTab);
    } catch {}
  }

  /**
   * Determine if a tab with id is already in array
   * @param id
   */
  isTabOpen(id: string): boolean {
    return this.tabs().some((tab) => tab.id === id);
  }

  /**
   * Detects if a tab already has a webfaced application
   * already open.
   */
  isApplicationOpen(): boolean {
    return this.tabs().some((tab) => {
      return tab.data.menuOption && tab.data.menuOption.object && tab.componentType === MENU_OPTIONS.IFRAME.id;
    });
  }

  /**
   * Adds a new tab to the tabs array
   * @param tab
   */
  addTab(tab: Tab): void {
    // Pre-initialize pdf-viewer as a hidden tab to speed up loading.
    if (this.isPdfTab(tab.id) && !this.isTabOpen(MENU_OPTIONS.PDF_VIEWER.id)) {
      tab.hidden.set(true);
    }

    this.tabs().push(tab);

    this.handleTabFocus(tab);

    this.swService.checkForUpdates();
  }

  handleTabFocus(tab: Tab) {
    const currentTab = this.activeTab;
    this.setFocus(tab);

    // If the tab is hidden we need to focus it for it to render behind the scenes and then switch to the tab
    // we want selected
    if (tab.hidden()) {
      setTimeout(() => {
        this.setFocus(currentTab);
      });
    }
  }

  /**
   * Set focus to tab object
   * @param tab
   */
  setFocus(tab: Tab): void {
    this.selectedIndex = this.tabs().findIndex((t) => t === tab);
  }

  /**
   * Focus the PDF tab
   */
  setFocusPdfTab() {
    if (this.pdfTab) {
      this.setFocus(this.pdfTab);
    }
  }

  /**
   * Open a new tab or set focus to an existing tab
   */
  openTab(id: string, tab: Tab, data?: any, additionalData?: any): void {
    const previousTab = this.activeTab;
    const tabIndex = this.tabs().findIndex((t) => t.id === id);
    this.selectedIndex = tabIndex;
    this.activeTab?.hidden?.set(false);

    // Store previous tab to reference when closing viewer.
    if (this.isPdfTab(this.activeTab?.id)) {
      this.activeTab.previousTab = previousTab;
    }

    if (!this.isTabOpen(id) && tab) {
      this.addTab(tab);
    }

    // Trigger tab to refresh on data change
    if (data || additionalData) {
      this.activeTab.data.data = data ? { ...data } : undefined;
      this.activeTab.additionalData = additionalData ? { ...additionalData } : undefined;
      this.refreshActiveTab.next();
    }
  }

  /**
   * Clear all tabs and alert user if a tab could not be closed
   */
  clearTabs(force?: boolean) {
    if (force) {
      this.tabs.set([]);
      return true;
    }

    let closeAll: boolean;

    if (this.authService.token) {
      for (const tab of this.tabs()) {
        if (tab.componentType == MENU_OPTIONS.IFRAME.id) {
          this.setFocus(tab);
          this.notifyService.warning("APP.MSG_WARNING", "APP.MSG_CLOSE_ALL_IFRAMES");
          closeAll = true;
        } else {
          if (tab.form && tab.form.dirty) {
            this.setFocus(tab);
            this.notifyService.warning("APP.MSG_WARNING", "APP.MSG_FORM_DIRTY");
            closeAll = true;
          }
        }
      }
    }

    //  Dont continue if we find any open iFrames or dirty forms
    if (closeAll) {
      return false;
    }

    this.tabs.set([]);
    return true;
  }

  /**
   * Return true if the tab is pdf viewer
   * @param id
   * @returns
   */
  isPdfTab = (id: string) => {
    return id === MENU_OPTIONS.PDF_VIEWER.id;
  };

  /**
   * Return true if the tab is webfaced
   * @param componentType
   * @returns
   */
  isWebFaceTab = (componentType: string) => {
    return componentType === MENU_OPTIONS.IFRAME.id;
  };

  /**
   * Retrieve the current active tab
   */
  get activeTab(): Tab {
    return this.tabs()[this.selectedIndex];
  }

  /**
   * Retrieve the current tabs menu option (if any)
   */
  get activeTabMenuOption(): MenuOption {
    return this.activeTab?.data?.menuOption ?? null;
  }

  /**
   * Retrieve the current tabs menu option application (if any)
   */
  get activeMenuOptionApplication(): string {
    return this.activeTabMenuOption?.application ?? null;
  }

  /**
   * Set the current tabs menu option application
   */
  set activeMenuOptionApplication(appId: string) {
    this.activeTabMenuOption.application = appId;
  }

  get pdfTab() {
    return this.tabs().find((x) => x.id === MENU_OPTIONS.PDF_VIEWER.id);
  }

  get externalWindowService(): ExternalWindowHandlerService {
    return AppInjector.get(ExternalWindowHandlerService);
  }

  /**
   * Remove tab from tab array
   * @param tab
   */
  private _destroyTab(tab: Tab, isWebFaceClosing?: boolean): void {
    if (!tab) {
      return;
    }

    // Get index of closing tab before its removed
    const tabIndex = this.tabs().findIndex((t) => t === tab);

    // When webface and not closing we need to send a message to iframe tab to close
    if (this.isWebFaceTab(tab.componentType) && !isWebFaceClosing) {
      this._sendWebFaceLogOffMessage(tab);
      return;
    } else if (this.isPdfTab(tab.id)) {
      // Hide pdf tab and set focus to previous clicked tab
      tab.hidden.set(true);
      if (tab?.previousTab) {
        this.setFocus(tab.previousTab);
      }
    } else {
      this._removeTab(tab);
    }

    // Refreshes html if there are no tabs
    if (this.tabs().length == 0) {
      this.ng.run(() => {
        this.router.navigate([ROUTES.HOME]);
      });
      return;
    }

    if (this.selectedIndex === tabIndex && this.selectedIndex !== 0) {
      const tempIndex = this.selectedIndex - 1;
      if (this.tabs()[tempIndex]?.hidden()) {
        this.selectedIndex -= 2;
      } else {
        this.selectedIndex -= 1;
      }
    }
  }

  /**
   * Send message to iframe component that will set url to log off webface screen properly
   * @param tab
   */
  private _sendWebFaceLogOffMessage(tab: Tab) {
    const key: string = "WFInvocation.do?";
    const results: string[] = (tab.data.data as string)?.split(key);
    if (results?.length) {
      this.messageService.sendWebfaceUrlMessage(`${results[0]}webfacing/WebFacing.do?AID=LOGOFF`);
      this.setFocus(tab);
    }
  }

  /**
   * Remove tab from tabs array
   * @param tab
   */
  private _removeTab(tab: Tab) {
    this.tabs.set(this.tabs().filter((t) => t !== tab));
  }
}
